import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Form, Button, notification } from 'antd';
import EmployeeReportForm from '../EmployeeReportForm';
import { ModalProps } from '@/types/ModalProps';
import {
  ReportModalEmployeeFormValue,
  ReportModalEmployeeParams,
} from '@/core/domain/reports/people/Report.types';
import { useDefaultFormErrorNotification, useReportGenerate } from '@/hooks';
import { useGenerateEmployeeReport } from '@/core/domain/reports/people/Report.hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import SpaceForButton from '@/components/_common/SpaceForButtons';

function EmployeeReportModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();
  const [form] = Form.useForm<ReportModalEmployeeFormValue>();
  const report = useReportGenerate();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();

  const mutation = useGenerateEmployeeReport();

  const handleGenerateReport = async (
    format: FileType,

    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const { birthday, specificDate, ...values } = await form.validateFields();

      const obj: ReportModalEmployeeParams = {
        ...values,
        format: FILE_FORMAT[format],
        fromBirthdayDate:
          birthday && birthday[0].format(specificDate ? 'YYYY-MM-DD' : 'MM-DD'),
        toBirthdayDate:
          birthday && birthday[1].format(specificDate ? 'YYYY-MM-DD' : 'MM-DD'),
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      notification.success({
        message: intl.formatMessage({
          id: 'employee.notification.generate.report.title',
        }),

        description: intl.formatMessage({
          id: 'employee.notification.generate.report.description',
        }),
      });

      handleVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }

    setFileUnloading(format);
  };

  return (
    <Modal
      width={680}
      visible={visible}
      onCancel={handleVisible}
      title={intl.formatMessage({ id: 'employee.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="end">
          <Button
            type="primary"
            key="viewReport"
            block
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'Funcionários.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
            loading={fileLoading.REPORT_VIEW}
          >
            <FormattedMessage id="employee.modal.report.buttons.viewReport" />
          </Button>

          <Button
            block
            type="primary"
            key="pdf"
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Funcionários.pdf',
                }),
              )
            }
            loading={fileLoading.PDF}
          >
            <FormattedMessage id="employee.modal.report.buttons.pdf" />
          </Button>

          <Button
            block
            type="primary"
            key="csv"
            onClick={() =>
              handleGenerateReport('CSV', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Funcionários.csv',
                }),
              )
            }
            loading={fileLoading.CSV}
          >
            <FormattedMessage id="employee.modal.report.buttons.csv" />
          </Button>
        </SpaceForButton>
      }
    >
      <EmployeeReportForm form={form} />
    </Modal>
  );
}

export default EmployeeReportModal;
